import { AccountUser } from '@travelata-libs/travelata-frontend';
import DepartureCitiesRepository from "@/js/common/Domain/Repository/DepartureCities";
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import { getPanelList, getCurrentCity } from '@entities/switch-city';
import ResortsRepository from '@/js/common/Domain/Repository/Resorts';
import HotelsRepository from '@/js/common/Domain/Repository/Hotels';
import eventBus from '@ve/services/eventBus';
import account from '@/js/desktop/components/account';
import Vue, { reactive } from 'vue';
import type { Store } from 'vuex';
import ResortFactory from '@/js/common/Domain/Factory/Resort';
import Backbone from 'backbone';
import getStore from '@ve/services/getStore/getStore';
declare let window: any;

import LibSearchFormHost from '../ui/LibSearchFormHost.vue';
import { IMapForSearchFormState } from '@fr/vue/pages/serp/store';
import { NightRange, TouristGroup } from '@fr/vue/types';

const HOST_ANCHOR_SELECTOR = '#js-lib-search-form-host';

interface SearchControllerLike {
    model: Backbone.Model;
    formSubmit(): void;
}

export interface MountLibSearchFormOptions {
    store?: Store<unknown>;
    controller: SearchControllerLike;
    disabledDestination?: boolean
}

export interface MountLibSearchFormResult {
    unmount(): void;
}

interface LibSearchFormHostExpose {
    validate(): boolean;
}

function getModelIds(model: Backbone.Model, field: string): number[] {
    const collection = model.get(field)?.get('model');
    const models: Backbone.Model[] = collection?.models ?? [];
    return models.map((item) => Number(item.get('id'))).sort((a, b) => a - b);
}

function isSameSelection(incoming: Array<{ id?: number | string }>, currentIds: number[]): boolean {
    if (incoming.length !== currentIds.length) {
        return false;
    }
    const incomingIds = incoming.map(({ id }) => Number(id)).sort((a, b) => a - b);
    return incomingIds.every((id, index) => id === currentIds[index]);
}

export function mountLibSearchForm(options: MountLibSearchFormOptions) {
    const { controller } = options;
    const store = options.store ?? getStore();
    const cities = getPanelList();
    const currentCity = getCurrentCity(cities);
    const authData = reactive<{ user: AccountUser | null, accountUrl: string; }>({
        user: null,
        accountUrl: window.appConfig.accountUrl,
    });

    eventBus.$on('authorize', (user: AccountUser) => {
        authData.user = user
    })

    const rootVm = new Vue({
        name: 'LibSearchFormHostRoot',
        store,
        render: (createElement) => createElement(LibSearchFormHost, {
            ref: 'host',
            props: {
                cities,
                currentCity,
                authData,
                disabledDestination: options.disabledDestination,
            },
            on: {
                search: () => onSearch(),
                city: (city: IMapForSearchFormState['departureCity']) => {
                    const departureCity = DepartureCitiesRepository.repository.get(city.id);
                    controller.model.get('departureCity').set('model', departureCity);
                },
                nights: (data: NightRange) => {
                    controller.model.get('nightRange').set('model', new Backbone.Model(data));
                },
                tourists: (data: TouristGroup) => {
                    controller.model.get('touristGroup').set('model', new Backbone.Model(data));
                },
                checkInDate: (data: IMapForSearchFormState['checkInDateRange']) => {
                    controller.model.get('checkInDateRange').set('model', new Backbone.Model(data));
                },
                resorts: (resorts: any[]) => {
                    controller.model.get('hotels').set('model', HotelsRepository.repository.getHotelsBuild([]));
                    if (isSameSelection(resorts, getModelIds(controller.model, 'resorts'))) {
                        return;
                    }
                    const country = CountriesRepository.repository.get(resorts[0]?.countryId);
                    const backboneResorts = new Backbone.Collection(resorts.map((r) => new ResortFactory({ ...r, country: country.attributes })))
                    controller.model.get('resorts').set('model', backboneResorts);
                    controller.model.get('countries').set('model', new Backbone.Collection([country]));
                },
                countries: (countries: IMapForSearchFormState['countries']) => {
                    controller.model.get('hotels').set('model', HotelsRepository.repository.getHotelsBuild([]));
                    controller.model.get('resorts').set('model', new Backbone.Collection([]));
                    if(countries[0].resorts.length) {
                        ResortsRepository.repository.addCountryResorts(countries[0].resorts, countries[0].id);
                    }
                    if (isSameSelection(countries, getModelIds(controller.model, 'countries'))) {
                        return;
                    }
                    const country = CountriesRepository.repository.get(countries[0].id);
                    controller.model.get('countries').set('model', new Backbone.Collection([country]));
                },
                hotels: (hotels: IMapForSearchFormState['hotels']) => {
                    if (isSameSelection(hotels, getModelIds(controller.model, 'hotels'))) {
                        return;
                    }
                    const modelsHotel = HotelsRepository.repository.getHotelsBuild(hotels);
                    controller.model.get('hotels').set('model', modelsHotel);
                },
                login: () => {
                    account.topMenuBar.openAuthForm();
                },
                logout: () => {
                    authData.user = null;
                    account.topMenuBar.logout();
                },
                buy: () => {
                    $('.openGeneralLeadForm')?.click();
                }
            },
        }),
    });

    function getHost(): LibSearchFormHostExpose | null {
        return (rootVm.$refs.host as unknown as LibSearchFormHostExpose | undefined) ?? null;
    }

    function onSearch(): void {
        const host = getHost();
        const isFormValid = host ? host.validate() : true;
        if (!isFormValid) {
            return;
        }
        controller.formSubmit();
    }
    const anchor = typeof document === 'undefined' ? null : document.querySelector(HOST_ANCHOR_SELECTOR);
    if (anchor) {
        rootVm.$mount(anchor as Element);
    }
}
